import {
  EmailTemplateFormValues,
  EmailTemplateStyle,
  EmailTemplateStyleFormValues,
  EmailTemplateStylePayload,
  EmailTemplateUpdatePayload,
  EmailTemplate,
  ActivityType,
  Category,
  CoverExtra,
  CoverType,
  Product,
  Provider,
} from '../types';

export * from './provider-city-prices';

// ActivityTypeList.tsx
export function applySortFilterList({
  tableData,
  filterName,
  filterStatus,
}: {
  tableData: ActivityType[];
  filterName: string;
  filterStatus: string;
}) {
  if (filterName) {
    tableData = tableData.filter(
      ({ name }) => name.toLowerCase().indexOf(filterName.toLowerCase()) !== -1
    );
  }

  if (filterStatus !== 'all') {
    tableData = tableData.filter(({ active }) =>
      active === true
        ? filterStatus.toLowerCase() === 'active'
        : filterStatus.toLowerCase() === 'inactive'
    );
  }
  return tableData;
}

export function applySortFilterForTemplates({
  tableData,
  filterText,
  filterStatus,
}: {
  tableData: EmailTemplate[];
  filterText: string;
  filterStatus: string;
}) {
  const normalizedFilterText = filterText.trim().toLowerCase();

  if (filterText) {
    tableData = tableData.filter((template) => {
      const searchableText = [
        template.name,
        template.ref,
        template.description,
        template.subject,
        template.custom_subject,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.indexOf(normalizedFilterText) !== -1;
    });
  }

  if (filterStatus !== 'all') {
    tableData = tableData.filter(
      ({ enabled }) => enabled === (filterStatus === 'enabled')
    );
  }

  return tableData;
}

const hasPresentValue = (value?: string | null) =>
  typeof value === 'string' && value.trim().length > 0;

const customValueOrNull = (value: string) => {
  const trimmedValue = value.trim();
  return trimmedValue.length ? trimmedValue : null;
};

export const EMAIL_TEMPLATE_THEME_DEFAULTS = {
  page_background_color: '#f8fafc',
  card_background_color: '#ffffff',
  border_color: '#e2e8f0',
  text_color: '#0f172a',
  muted_text_color: '#475569',
  accent_color: '#ff8177',
  button_text_color: '#ffffff',
};

export const getEmailTemplateEffectiveSubject = (template: EmailTemplate) =>
  template.subject;

export const getEmailTemplateEffectiveText = (template: EmailTemplate) =>
  template.body_text ?? '';

export const isEmailTemplateCustomized = (template: EmailTemplate) =>
  (hasPresentValue(template.custom_subject) &&
    template.subject.trim() !== String(template.custom_subject).trim()) ||
  (hasPresentValue(template.custom_body_html) &&
    (template.body_html ?? '').trim() !==
      String(template.custom_body_html).trim()) ||
  (hasPresentValue(template.custom_body_text) &&
    (template.body_text ?? '').trim() !==
      String(template.custom_body_text).trim());

export const emailTemplateToFormValues = (
  template: EmailTemplate
): EmailTemplateFormValues => ({
  subject: template.subject ?? '',
  body_html: template.body_html ?? '',
  body_text: template.body_text ?? '',
});

export const emailTemplateStyleToFormValues = (
  style?: EmailTemplateStyle | null
): EmailTemplateStyleFormValues => ({
  page_background_color:
    style?.page_background_color ??
    EMAIL_TEMPLATE_THEME_DEFAULTS.page_background_color,
  card_background_color:
    style?.card_background_color ??
    EMAIL_TEMPLATE_THEME_DEFAULTS.card_background_color,
  border_color:
    style?.border_color ?? EMAIL_TEMPLATE_THEME_DEFAULTS.border_color,
  text_color: style?.text_color ?? EMAIL_TEMPLATE_THEME_DEFAULTS.text_color,
  muted_text_color:
    style?.muted_text_color ?? EMAIL_TEMPLATE_THEME_DEFAULTS.muted_text_color,
  accent_color:
    style?.accent_color ?? EMAIL_TEMPLATE_THEME_DEFAULTS.accent_color,
  button_text_color:
    style?.button_text_color ?? EMAIL_TEMPLATE_THEME_DEFAULTS.button_text_color,
  logo_enabled: Boolean(style?.logo_enabled),
  logo_url: style?.logo_url ?? '',
});

export const emailTemplateStyleFormToPayload = (
  values: EmailTemplateStyleFormValues
): EmailTemplateStylePayload => ({
  page_background_color: customValueOrNull(values.page_background_color),
  card_background_color: customValueOrNull(values.card_background_color),
  border_color: customValueOrNull(values.border_color),
  text_color: customValueOrNull(values.text_color),
  muted_text_color: customValueOrNull(values.muted_text_color),
  accent_color: customValueOrNull(values.accent_color),
  button_text_color: customValueOrNull(values.button_text_color),
  logo_enabled: values.logo_enabled,
  logo_url: customValueOrNull(values.logo_url),
});

export const emailTemplateFormToPayload = (
  values: EmailTemplateFormValues
): EmailTemplateUpdatePayload => ({
  subject: values.subject.trim(),
  body_html: customValueOrNull(values.body_html),
  body_text: customValueOrNull(values.body_text),
});

export const getEmailTemplateStyleResetPayload =
  (): EmailTemplateStylePayload => ({
    ...EMAIL_TEMPLATE_THEME_DEFAULTS,
    logo_enabled: false,
    logo_url: null,
  });

export const getEmailTemplateRevertPayload =
  (): EmailTemplateUpdatePayload => ({
    custom_subject: null,
    custom_body_html: null,
    custom_body_text: null,
  });

export const formatEmailTemplateVariables = (template: EmailTemplate) =>
  template.variables ? JSON.stringify(template.variables, null, 2) : '';

// CategoryList.tsx
export function applySortFilterForCategoryList({
  tableData,
  filterName,
  filterStatus,
}: {
  tableData: Category[];

  filterName: string;
  filterStatus: string;
}) {
  if (filterName) {
    tableData = tableData.filter(
      ({ name, description }) =>
        name.toLowerCase().indexOf(filterName.toLowerCase()) !== -1 ||
        description.toLowerCase().indexOf(filterName.toLowerCase()) !== -1
    );
  }

  if (filterStatus !== 'all') {
    tableData = tableData.filter(({ active }) =>
      active === true
        ? filterStatus.toLowerCase() === 'active'
        : filterStatus.toLowerCase() === 'inactive'
    );
  }
  return tableData;
}

// ProductList.tsx
export function applySortFilterForProductList({
  tableData,
  filterName,
  filterCategory,
  filterStatus,
}: {
  tableData: Product[];
  filterName: string;
  filterCategory: string;
  filterStatus: string;
}) {
  if (filterName) {
    tableData = tableData.filter(
      ({ name, description, provider: { name: providerName } }) =>
        name.toLowerCase().indexOf(filterName.toLowerCase()) !== -1 ||
        description.toLowerCase().indexOf(filterName.toLowerCase()) !== -1 ||
        providerName.toLowerCase().indexOf(filterName.toLowerCase()) !== -1
    );
  }

  if (filterStatus !== 'all') {
    tableData = tableData.filter(({ active }) =>
      active === true
        ? filterStatus.toLowerCase() === 'active'
        : filterStatus.toLowerCase() === 'inactive'
    );
  }
  if (filterCategory !== 'all') {
    tableData = tableData.filter(
      ({ name }) =>
        name.toLowerCase().indexOf(filterCategory.toLowerCase()) !== -1
    );
  }
  return tableData;
}

// ProviderList.tsx
export function applySortFilterProviderList({
  tableData,
  filterName,
  filterStatus,
}: {
  tableData: Provider[];
  filterName: string;
  filterStatus: string;
}) {
  if (filterName) {
    tableData = tableData.filter(
      ({ name, ref }) =>
        name.toLowerCase().indexOf(filterName.toLowerCase()) !== -1 ||
        ref.toLowerCase().indexOf(filterName.toLowerCase()) !== -1
    );
  }

  if (filterStatus !== 'all') {
    tableData = tableData.filter(({ active }) =>
      active === true
        ? filterStatus.toLowerCase() === 'active'
        : filterStatus.toLowerCase() === 'inactive'
    );
  }
  return tableData;
}

// CoverLevelType
export function applySortFilterCoverLevelList({
  tableData,
  filterName,
  filterStatus,
}: {
  tableData: CoverType[];
  filterName: string;
  filterStatus: string;
}) {
  if (filterName) {
    tableData = tableData.filter(
      ({ name }) => name.toLowerCase().indexOf(filterName.toLowerCase()) !== -1
    );
  }

  if (filterStatus !== 'all') {
    tableData = tableData.filter(({ active }) =>
      active === true
        ? filterStatus.toLowerCase() === 'active'
        : filterStatus.toLowerCase() === 'inactive'
    );
  }
  return tableData;
}

// CoverLevelExtrtas
export function applySortFilterCoverLevelExtrasList({
  tableData,
  filterName,
  filterStatus,
  filterCoverLevel,
}: {
  tableData: CoverExtra[];
  filterName: string;
  filterStatus: string;
  filterCoverLevel: string;
}) {
  if (filterName) {
    tableData = tableData.filter(
      ({ name, extra_type: { name: extraTypeName } }) =>
        name.toLowerCase().indexOf(filterName.toLowerCase()) !== -1 ||
        extraTypeName.toLowerCase().indexOf(filterName.toLowerCase()) !== -1
    );
  }
  if (filterCoverLevel !== 'all') {
    tableData = tableData.filter(
      ({ name }) =>
        name.toLowerCase().indexOf(filterCoverLevel.toLowerCase()) !== -1
    );
  }

  if (filterStatus !== 'all') {
    tableData = tableData.filter(({ active }) =>
      active === true
        ? filterStatus.toLowerCase() === 'active'
        : filterStatus.toLowerCase() === 'inactive'
    );
  }
  return tableData;
}
